// On-device cache of the WiserWeb account blobs, for apps that persist a session and want OFFLINE unlock.
// After sign-in the account's WRAPPED blobs (master key under the KEK, private key under the MK, public key,
// salt, email) are persisted so a later session can `unlock(password, blobs)` with no server round-trip. The
// cached data is NOT secret: every wrapped field is useless without the password (Argon2id → KEK). Pluggable
// store: localStorage by default (small, non-secret, config-like); an in-memory impl for tests / SSR.
//
// Auth-only apps (no local encryption — e.g. wiserwebdns) don't need this at all; they just trade the
// wiser-accounts session for their own and never cache.
import type { AccountBlobs } from '@wiser/accounts';

export interface BlobStore {
  get(key: string): Promise<string | null>;
  set(key: string, val: string): Promise<void>;
  del(key: string): Promise<void>;
}

let cacheKey = 'wiser-account-blobs';
/** Override the storage key (e.g. namespace per app). */
export function setCacheKey(key: string): void { cacheKey = key; }

function localStorageStore(): BlobStore {
  return {
    async get(k) { try { return localStorage.getItem(k); } catch { return null; } },
    async set(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
    async del(k) { try { localStorage.removeItem(k); } catch { /* private mode */ } },
  };
}

/** In-memory store for tests / SSR. */
export function memoryStore(): BlobStore {
  const m = new Map<string, string>();
  return {
    async get(k) { return m.get(k) ?? null; },
    async set(k, v) { m.set(k, v); },
    async del(k) { m.delete(k); },
  };
}

let store: BlobStore = localStorageStore();
/** Swap the backend (tests / a future IndexedDB impl). */
export function setBlobStore(s: BlobStore): void { store = s; }

export async function cacheAccount(blobs: AccountBlobs): Promise<void> {
  await store.set(cacheKey, JSON.stringify(blobs));
}

export async function loadCachedAccount(): Promise<AccountBlobs | null> {
  const raw = await store.get(cacheKey);
  if (!raw) return null;
  try { return JSON.parse(raw) as AccountBlobs; } catch { return null; }
}

export async function clearCachedAccount(): Promise<void> {
  await store.del(cacheKey);
}

// ── last-used username (sign-in convenience) ────────────────────────────────────────────────────────────
// Remember the last email a user signed in with, so the sign-in form can pre-fill it and a returning user
// only types their password. This is a NON-SECRET convenience string kept deliberately separate from the
// blob cache above, so AUTH-ONLY apps (that never `cacheAccount`) get it too. Same pluggable BlobStore.

let lastUsernameKey = 'wiser.last_username';
/** Override the last-username storage key (e.g. namespace per app: `wiser.last_username:office`). */
export function setLastUsernameKey(key: string): void { lastUsernameKey = key; }

/** Remember the email last used to sign in (call after a successful sign-in). */
export async function saveLastUsername(email: string): Promise<void> {
  const e = email.trim();
  if (e) await store.set(lastUsernameKey, e);
}

/** The last email used to sign in, or null if none remembered / storage unavailable. */
export async function loadLastUsername(): Promise<string | null> {
  return store.get(lastUsernameKey);
}

/** Forget the remembered username (wire into "Not you?" and full sign-out). */
export async function clearLastUsername(): Promise<void> {
  await store.del(lastUsernameKey);
}
